import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search } from 'lucide-react';
import { computeMenuPlacement, useMenuPlacement } from 'shared/components/ui/menuPlacement';

/**
 * BrandDropdown — the canonical brand-styled dropdown.
 *
 * Use this anywhere we'd otherwise reach for a native <select>, so every
 * picker across Agents / Models / Evals / etc. looks and behaves identically.
 *
 * The menu is portaled to <body> and positioned fixed against the trigger.
 * An absolutely-positioned menu gets clipped by any scrolling ancestor
 * (MUI's DialogContent is overflow-y:auto, so the menu was trapped inside the
 * modal), and no z-index can escape a clip. Portaling also means the menu
 * must out-rank MUI's Dialog (z-index 1300) — hence MENU_Z below.
 *
 * Props:
 *   value          string | null
 *   onChange       (value) => void
 *   options        Array<{ value, label, monospace?, icon?: ReactNode, hint?: string }>
 *                     OR
 *                  Array<{ groupLabel, options: [...] }>     (grouped mode)
 *   placeholder    string                                     ('Select…')
 *   searchable     boolean                                    (default false — auto-on if 8+ options)
 *   searchPlaceholder string                                  ('Search…')
 *   disabled       boolean
 *   triggerClassName string                                   extra classes for the trigger button
 *   menuClassName  string                                     extra classes for the dropdown panel
 *   maxHeight      number                                     px; default 280
 *   renderTrigger  (selectedOption?) => ReactNode | string    custom trigger label render
 *   renderOption   (option) => ReactNode                      custom option render
 *   compact        boolean                                    smaller trigger for inline chips
 *   menuMinWidth   number                                     menu width (px); defaults to trigger width
 *   visibleLimit   number                                     render at most N options at a time
 *                                                             (applied AFTER filtering, so search still
 *                                                             reaches every option). Off by default.
 *   loading        boolean                                    options are still arriving; shows a note
 *                                                             so a short list doesn't read as the whole set
 *   ariaLabel      string                                     names the trigger for assistive tech when
 *                                                             there's no associated <label> (the trigger is
 *                                                             a button, so htmlFor can't reach it)
 */
/** Above MUI Dialog (1300) and Drawer (1200), below MUI Tooltip (1500). */
const MENU_Z = 1400;
/** Don't flip unless the menu would be uselessly short on the preferred side. */
const MIN_MENU_H = 180;

export default function BrandDropdown({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  searchable,
  searchPlaceholder = 'Search…',
  disabled = false,
  triggerClassName = '',
  menuClassName = '',
  maxHeight = 280,
  renderTrigger,
  renderOption,
  footer,
  compact = false,
  menuMinWidth,
  visibleLimit,
  loading = false,
  emptyMessage = 'No matches.',
  ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  // A surrounding dialog can ask every menu in it to open upward (MenuPlacementProvider).
  const placement = useMenuPlacement();

  const groups = useMemo(() => normalizeGroups(options), [options]);
  const flat = useMemo(() => groups.flatMap((g) => g.options), [groups]);
  const totalCount = flat.length;
  const autoSearchable = searchable ?? totalCount >= 8;

  const selected = useMemo(
    () => flat.find((o) => String(o.value) === String(value)),
    [flat, value],
  );

  /** Anchor the fixed menu to the trigger, on the preferred side unless cramped there. */
  const place = useCallback(() => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    setPos(computeMenuPlacement(r, { prefer: placement, width: menuMinWidth, maxHeight, minHeight: MIN_MENU_H, floor: 120 }));
  }, [menuMinWidth, maxHeight, placement]);

  useEffect(() => {
    if (!open) return undefined;
    place();
    // The menu is fixed, so any ancestor scroll (a modal body, the page) would
    // otherwise leave it stranded. Capture catches scrolls on any ancestor.
    const onScroll = (e) => { if (!menuRef.current?.contains(e.target)) place(); };
    // A portaled menu is NOT inside wrapRef, so it must be checked separately
    // or mousedown on an option would close the menu before the click lands.
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    // preventDefault tells a surrounding modal this Escape closed the menu, not it.
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); setOpen(false); } };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, place]);

  const filteredGroups = useMemo(() => {
    if (!query) return groups;
    const q = query.toLowerCase();
    return groups
      .map((g) => ({
        ...g,
        options: g.options.filter((o) =>
          String(o.label || o.value).toLowerCase().includes(q)
          || String(o.hint || '').toLowerCase().includes(q)
        ),
      }))
      .filter((g) => g.options.length > 0);
  }, [groups, query]);

  const filteredCount = useMemo(
    () => filteredGroups.reduce((n, g) => n + g.options.length, 0),
    [filteredGroups],
  );

  // Cap AFTER filtering: the user can always search their way to an option
  // that isn't currently rendered.
  const visibleGroups = useMemo(() => {
    if (!visibleLimit || filteredCount <= visibleLimit) return filteredGroups;
    let left = visibleLimit;
    return filteredGroups
      .map((g) => {
        const take = g.options.slice(0, Math.max(0, left));
        left -= take.length;
        return { ...g, options: take };
      })
      .filter((g) => g.options.length > 0);
  }, [filteredGroups, visibleLimit, filteredCount]);

  const hiddenCount = visibleLimit ? Math.max(0, filteredCount - visibleLimit) : 0;

  const triggerLabel = renderTrigger
    ? renderTrigger(selected)
    : selected
      ? selected.label || selected.value
      : placeholder;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }} data-brand-dropdown>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`brand-dropdown-trigger ${triggerClassName}`}
        style={{
          width: '100%',
          display: 'flex', alignItems: 'center', gap: compact ? 6 : 8,
          textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer',
          padding: compact ? '2px 6px' : '6px 10px',
          border: '1px solid var(--border-default)', borderRadius: 6,
          background: disabled ? 'var(--surface-sunken)' : 'var(--surface-base)',
          fontSize: compact ? 12 : 13,
          color: 'var(--text-primary)',
          fontFamily: 'Inter, sans-serif',
          opacity: disabled ? 0.7 : 1,
        }}
      >
        {selected?.icon && <span style={{ display: 'inline-flex', flexShrink: 0 }}>{selected.icon}</span>}
        <span
          style={{
            flex: 1, minWidth: 0,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            fontFamily: selected?.monospace ? 'monospace' : 'inherit',
            color: selected ? 'var(--text-primary)' : 'var(--text-muted)',
          }}
        >
          {triggerLabel}
        </span>
        <ChevronDown style={{ width: compact ? 12 : 14, height: compact ? 12 : 14, color: 'var(--text-muted)', flexShrink: 0 }} />
      </button>

      {open && pos && createPortal(
        <div
          ref={menuRef}
          className={`brand-dropdown-menu ${menuClassName}`}
          role="listbox"
          aria-label={ariaLabel}
          style={{
            position: 'fixed', zIndex: MENU_Z,
            left: pos.left,
            top: pos.top,
            bottom: pos.bottom,
            width: pos.width,
            background: 'var(--surface-raised)',
            border: '1px solid var(--border-default)', borderRadius: 10,
            boxShadow: 'var(--shadow-lg)',
            maxHeight: pos.maxHeight, overflowY: 'auto',
            fontFamily: 'Inter, sans-serif',
            fontSize: compact ? 12 : 13,
          }}
        >
          {autoSearchable && (
            <div style={{
              position: 'sticky', top: 0, background: 'var(--surface-raised)',
              borderBottom: '1px solid var(--border-default)', padding: 8,
            }}>
              <div style={{ position: 'relative' }}>
                <Search style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', width: 12, height: 12, color: 'var(--text-muted)' }} />
                <input
                  autoFocus
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  style={{
                    width: '100%', padding: '6px 8px 6px 28px',
                    border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12,
                    color: 'var(--text-primary)', outline: 'none', background: 'var(--surface-base)',
                    fontFamily: 'inherit',
                  }}
                />
              </div>
            </div>
          )}

          {filteredGroups.length === 0 && (
            <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
              {loading ? 'Loading…' : emptyMessage}
            </div>
          )}

          {visibleGroups.map((g, gi) => (
            <div key={g.groupLabel || `g-${gi}`} style={{ padding: '4px 0' }}
              role={g.groupLabel ? 'group' : 'presentation'} aria-label={g.groupLabel || undefined}>
              {g.groupLabel && (
                <div aria-hidden="true" style={{
                  padding: '4px 12px', fontSize: 10, fontWeight: 600,
                  color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: 0.5,
                }}>
                  {g.groupLabel}
                </div>
              )}
              {g.options.map((opt) => {
                const isSel = String(opt.value) === String(value);
                return (
                  <button
                    key={String(opt.value)}
                    type="button"
                    // The trigger has promised a listbox since this component
                    // was written; without these the promise went unkept and a
                    // screen reader met an unlabelled pile of buttons with no
                    // count, no position and no way to tell which was chosen.
                    role="option"
                    aria-selected={isSel}
                    onClick={() => { onChange(opt.value); setOpen(false); setQuery(''); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '8px 12px',
                      background: isSel ? 'var(--surface-hover)' : 'transparent',
                      border: 'none', cursor: 'pointer', textAlign: 'left',
                      fontSize: 13, color: 'var(--text-primary)',
                      fontFamily: opt.monospace ? 'monospace' : 'inherit',
                    }}
                    onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = 'var(--surface-hover)'; }}
                    onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}
                  >
                    {opt.icon && <span style={{ display: 'inline-flex', flexShrink: 0 }}>{opt.icon}</span>}
                    {renderOption ? renderOption(opt) : (
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{
                          display: 'block', overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {opt.label || opt.value}
                        </span>
                        {opt.hint && (
                          <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {opt.hint}
                          </span>
                        )}
                      </span>
                    )}
                    {isSel && <Check style={{ width: 12, height: 12, color: 'var(--text-primary)', flexShrink: 0 }} />}
                  </button>
                );
              })}
            </div>
          ))}

          {/* Never let a capped or still-loading list read as the full set. */}
          {(hiddenCount > 0 || (loading && filteredCount > 0)) && (
            <div
              aria-live="polite"
              style={{
                padding: '6px 12px', borderTop: '1px solid var(--border-subtle)',
                color: 'var(--text-muted)', fontSize: 11,
              }}
            >
              {loading
                ? `Showing ${filteredCount - hiddenCount} so far: still loading…`
                : `Showing ${visibleLimit} of ${filteredCount}: type to find more`}
            </div>
          )}

          {footer && (
            <div style={{
              position: 'sticky', bottom: 0, background: 'var(--surface-raised)',
              borderTop: '1px solid var(--border-default)', padding: 6,
            }}>
              {footer(() => setOpen(false))}
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

function normalizeGroups(options) {
  if (!Array.isArray(options) || options.length === 0) return [];
  if (options[0] && Array.isArray(options[0].options)) return options;
  return [{ groupLabel: null, options }];
}
